/**
 * Утилиты для парсинга Instagram URL
 */

const INSTAGRAM_URL_PATTERN =
  /instagram\.com\/(?:[\w.]+\/)?(?:p|reel|reels)\/([A-Za-z0-9_-]+)/;
const INSTAGRAM_SHORTCODE_LENGTH = 11;

const INSTAGRAM_FULL_URL_PATTERN =
  /https?:\/\/(?:www\.)?instagram\.com\/[^\s]*/;

const TIKTOK_URL_PATTERN = /tiktok\.com\/@[\w.]+\/video\/(\d+)/;
const TIKTOK_FULL_URL_PATTERN = /https?:\/\/(?:www\.)?tiktok\.com\/[^\s]*/;

// TikTok video link → { url, key }. Key is "tt:<videoId>" so it dedups
// independently of Instagram shortcodes and routes to the TikTok path later.
function extractTikTok(line: string): { url: string; key: string } | null {
  const idMatch = line.match(TIKTOK_URL_PATTERN);
  if (!idMatch) return null;
  const url = line.match(TIKTOK_FULL_URL_PATTERN)?.[0] ?? line;
  return { url, key: `tt:${idMatch[1]}` };
}

export function extractShortcode(url: string): string | null {
  const match = url.match(INSTAGRAM_URL_PATTERN);
  return match ? match[1].slice(0, INSTAGRAM_SHORTCODE_LENGTH) : null;
}

export function extractInstagramUrl(line: string): string | null {
  const match = line.match(INSTAGRAM_FULL_URL_PATTERN);
  return match ? match[0] : null;
}

function normalizeInstagramUrl(url: string): string | null {
  const shortcode = extractShortcode(url);
  if (!shortcode) return null;
  return url.replace(INSTAGRAM_URL_PATTERN, (match) => {
    const prefix = match.replace(/([A-Za-z0-9_-]+)$/, '');
    return `${prefix}${shortcode}`;
  });
}

export function isValidInstagramUrl(url: string): boolean {
  return INSTAGRAM_URL_PATTERN.test(url);
}

export function parseUrlList(text: string): {
  valid: Array<{ url: string; shortcode: string }>;
  invalid: string[];
  duplicates: string[];
} {
  const lines = text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  const seenShortcodes = new Set<string>();
  const valid: Array<{ url: string; shortcode: string }> = [];
  const invalid: string[] = [];
  const duplicates: string[] = [];

  for (const line of lines) {
    const url = extractInstagramUrl(line);
    if (url) {
      const shortcode = extractShortcode(url);
      if (!shortcode) {
        invalid.push(line);
        continue;
      }
      if (seenShortcodes.has(shortcode)) {
        duplicates.push(url);
        continue;
      }
      seenShortcodes.add(shortcode);
      valid.push({ url, shortcode });
      continue;
    }

    const tiktok = extractTikTok(line);
    if (tiktok) {
      if (seenShortcodes.has(tiktok.key)) {
        duplicates.push(tiktok.url);
        continue;
      }
      seenShortcodes.add(tiktok.key);
      valid.push({ url: tiktok.url, shortcode: tiktok.key });
      continue;
    }

    invalid.push(line);
  }

  return { valid, invalid, duplicates };
}

function extractValidUrls(block: string): string[] {
  const lines = block.split('\n').map(l => l.trim()).filter(Boolean);
  const seenShortcodes = new Set<string>();
  const validUrls: string[] = [];
  for (const line of lines) {
    const url = extractInstagramUrl(line);
    if (url) {
      const shortcode = extractShortcode(url);
      const normalizedUrl = normalizeInstagramUrl(url);
      if (!shortcode || !normalizedUrl || seenShortcodes.has(shortcode)) continue;
      seenShortcodes.add(shortcode);
      validUrls.push(normalizedUrl);
      continue;
    }
    const tiktok = extractTikTok(line);
    if (tiktok && !seenShortcodes.has(tiktok.key)) {
      seenShortcodes.add(tiktok.key);
      validUrls.push(tiktok.url);
    }
  }
  return validUrls;
}

/**
 * Parses textarea text into pages and sub-groups.
 * \n\n  (1 blank line)  = sub-group separator within the same page → determines image sources
 * \n\n\n (2 blank lines) = page separator → new page in the Google Doc
 * Returns: pages → sub-groups → URLs
 */
export function parsePages(text: string): string[][][] {
  const rawPages = text.split(/\n{3,}/);
  return rawPages
    .map(pageBlock => {
      const subGroups = pageBlock.split(/\n{2}/).map(extractValidUrls).filter(sg => sg.length > 0);
      return subGroups;
    })
    .filter(page => page.length > 0);
}
